import { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { FlexColumn, FlexRow } from '../../../atoms/layout/Flex'
import Label from '../../../atoms/display/Label'
import Dialog from '../../../atoms/feedback/Dialog'
import CircularProgress from '../../../atoms/feedback/CircularProgress'
import ImageViewer from '../../../atoms/display/ImageViewer'
import PdfViewer from '../../../atoms/display/PdfViewer'
import usePdf from '../../../../hooks/usePdf'
import { SessionSelectors } from '../../../../store/session/selectors'

type FilePreviewDialogProps = {
  file: { id: string; name: string } | null
  onClose: () => void
}

// ファイル名からPDFかどうかを判定
function isPdfFile(fileName: string): boolean {
  return fileName.toLowerCase().endsWith('.pdf')
}

export default function FilePreviewDialog({ file, onClose }: FilePreviewDialogProps) {
  const companyId = useSelector(SessionSelectors.companyId)
  const token = useSelector(SessionSelectors.token)
  const baseUrl = process.env.REACT_APP_API_BASE_URL
  const pdfParams = usePdf({})

  // 画像のblob URL
  const [imageBlobUrl, setImageBlobUrl] = useState<string | null>(null)
  // 画像読み込み中フラグ
  const [isLoadingImage, setIsLoadingImage] = useState(false)

  // ファイルのURLを生成
  const getFileUrl = useCallback(
    (fileId: string): string =>
      `${baseUrl ?? ''}/companies/${companyId ?? ''}/visitor-registration-form/files/${fileId}`,
    [baseUrl, companyId]
  )

  // 画像ファイルを認証付きで取得してblob URLを作成
  useEffect(() => {
    let objectUrl: string | null = null

    if (file !== null && !isPdfFile(file.name) && token !== undefined) {
      setIsLoadingImage(true)
      const fetchImage = async (): Promise<void> => {
        const response = await fetch(getFileUrl(file.id), {
          headers: {
            authorization: `Token ${token}`,
          },
        })
        const blob = await response.blob()
        objectUrl = URL.createObjectURL(blob)
        setImageBlobUrl(objectUrl)
        setIsLoadingImage(false)
      }

      void fetchImage()
    } else {
      setImageBlobUrl(null)
      setIsLoadingImage(false)
    }

    // クリーンアップ時にblob URLを解放
    return () => {
      if (objectUrl !== null) {
        URL.revokeObjectURL(objectUrl)
      }
    }
  }, [file, token, getFileUrl])

  if (token === undefined) {
    return null
  }

  return (
    <Dialog open={file !== null} width="lg" onClose={onClose}>
      {file && (
        <FlexColumn>
          <Label text={file.name} variant="subtitle" />
          {isPdfFile(file.name) && (
            <PdfViewer
              url={getFileUrl(file.id)}
              httpHeaders={{
                authorization: `Token ${token}`,
              }}
              height={600}
              defaultZoomLevel="pageWidth"
              params={pdfParams}
            />
          )}
          {!isPdfFile(file.name) && isLoadingImage && (
            <FlexRow justify="center" align="center" height={400}>
              <CircularProgress size="normal" />
            </FlexRow>
          )}
          {!isPdfFile(file.name) && !isLoadingImage && imageBlobUrl && (
            <ImageViewer src={imageBlobUrl} alt={file.name} controlsTop={50} />
          )}
        </FlexColumn>
      )}
    </Dialog>
  )
}
